import React, { useCallback } from "react"
import { toast } from "react-toastify"
import { useLocation, useNavigate } from "react-router"
import { Link } from "react-router-dom"
import { AssessmentSubmissionResponse, LessonSubmissionResponse, PublishGradeProps, TopicSubmissionResponse } from "shared/types/moduleTypes"
import modules from "shared/routes/moduleRoutes"
import fillTableData from "./helpers/fillTableData"
import Pane from "components/Dashboards/General/Pane"
import Col from "react-bootstrap/Col"
import Row from "react-bootstrap/Row"
import styles from "./studentSubmission.module.scss"
import { LessonResponse } from "shared/lessons"
import pureInquiry from "shared/routes/simulations/pureInquiry"
import { AssignmentSubmissionResponse, StudentAssignmentResponse } from "shared/types/studentTypes"
import { StudentSubmissionsState } from "components/Dashboards/Student/Submissions"
import student from "shared/routes/student"
import StudentLessonResponses from "./StudentResponses/StudentLessonResponses"
import StudentLessonTableResponses from "./StudentResponses/StudentLessonTableResponses"
import StudentAssessmentResponses from "./StudentResponses/StudentAssessmentResponses"
import { AssessmentViewOptionsType } from "shared/types/assessmentTypes"
import StudentTopicSectionResponses from "./StudentResponses/StudentTopicSectionResponses"
import { CurriculumTopic } from "shared/types/curriculumTypes"
import books from "shared/routes/curriculum/books"
import StudentSubmissionSidebar from "./StudentSubmissionSidebar"
import { startNewSubmission } from "helpers/getSubmissionId"

/**
 * Location state properties that this page is expecting when being redirected here
 */
export class SubmissionStudentState {
    constructor() {
        this.previousPage = ".."
        this.currentIndex = 1
        this.lesson = null
        this.submission = null
    }
    submission: AssignmentSubmissionResponse
    assignment: StudentAssignmentResponse
    studentId: string
    currentIndex?: number
    previousPage?: string
    lesson?: LessonResponse
}

export default function StudentSubmission() {
    const location = useLocation()
    const navigate = useNavigate()
    const state = location.state as SubmissionStudentState

    const { lesson, topicId } = state.assignment
    let { moduleName } = state.assignment

    const { id: studentLessonId } = state.submission.lesson
    const { studentTopicId } = state.submission.topic

    const [lessonSubmission, setLessonSubmission] = React.useState<LessonSubmissionResponse>(null)
    const [topicSubmission, setTopicSubmission] = React.useState<TopicSubmissionResponse>(null)
    const [assessmentSubmission, setAssessmentSubmission] = React.useState<AssessmentSubmissionResponse>(null)
    const [selectedStandards, setSelectedStandards] = React.useState<PublishGradeProps["standardsMastery"]>([])
    const [assessmentViewOption, setAssessmentViewOption] = React.useState<AssessmentViewOptionsType>(null)

    const [expand, setExpand] = React.useState(true)
    const [topic, setTopic] = React.useState<CurriculumTopic>(null)

    const assessmentOnly = Boolean(!state.assignment.topicId && !state.assignment?.lessonId && state.assignment.assessmentId )// no lesson, but has assessment

     // needed for navigating back a page
    const studentSubmissionsState: StudentSubmissionsState = {
        studentId: state.studentId,
        assignment: state.assignment,
        lesson: state.lesson
    }

    React.useEffect(() => {
        if (topicId) {
            modules.getTopicSubmission({ studentTopicId: studentTopicId})
                .then(data => {
                    setTopicSubmission(data)
                })

            books.getTopic({ topicId }).then(setTopic)
        }

        if (state.assignment.lessonId && studentLessonId) {
            modules.getLessonSubmission({ moduleName: lesson.moduleName, lessonNumber: lesson.number, lessonType: lesson.type, studentLessonId })
                .then((data) => {
                    data.grade = data.grade || (data.points / data.totalPoints * 100)

                    setLessonSubmission({
                        ...data
                    })
                    fillTableData()
                    setSelectedStandards([
                        ...selectedStandards, 
                        ...data.standardsMastery
                    ])
                }).catch((err) => {
                    toast.error(err?.response?.data?.message || "Could not retrieve submission")
                })
        } else if (state.assignment.lessonVariety === "Pure Inquiry") {
            pureInquiry.getFull({ pureInquiryId: studentLessonId }).then(({ data }) => {
                data.grade = data.grade || data.points / data.totalPoints * 100
                setLessonSubmission({
                    ...data
                    })
                //TODO: use other table setup
            })
        }
        
        if (!topicId && state.assignment.assessmentId) {
            modules.getAssessmentSubmission({assessmentId: state.assignment.assessmentId, studentId: state.studentId, submissionId: state.submission.id }).then((assessmentSubmissions) => {
                setAssessmentSubmission(assessmentSubmissions ? {...assessmentSubmissions} : null)
            }).catch((err) => {
                toast.error(err?.response?.data?.message)
            })
        }
    },
        //Justification: This hook should not run every time selectedStandards is changed
        //eslint-disable-next-line react-hooks/exhaustive-deps
        [moduleName, lesson?.number, studentLessonId])

    React.useEffect(() => {
        (async () => {
            const { studentAssessmentViewOption } = await student.getAssignment(state.assignment.id)
            setAssessmentViewOption(studentAssessmentViewOption)
        })()
    }, [state.assignment.id])

    const openAssignment = useCallback(async ({ assignment } : { assignment: StudentAssignmentResponse }) => {
        const newSubmissionId = await startNewSubmission(assignment.id, "reworking")
        
        try {
            navigate("/dashboard/student/assignments/assignment", {
                state: {
                    submissionId: newSubmissionId,
                    assignment: assignment,
                    continueAssessment: false,
                    reworkAssignmentMode: true,
                    isReview: false,
                }
            })
        } catch (err) {
            toast.error(err?.response?.data?.message ?? "Unable to start assignment, please try again.")
        }
    }, [navigate])

    React.useEffect(() => {
        const sectionsWithTables: string[] = Array.from(topicSubmission?.tableResponses.reduce((prev, resp) => {
            prev.add(resp.sectionId)
            return prev
        }, new Set<string>()) ?? [])

        const inputsBySection: Record<string, HTMLInputElement[]> = sectionsWithTables.reduce((prev, sectionId) => {
            prev[sectionId] = [...document
                .querySelectorAll(`#section-pane-${sectionId} .question-content input`)]
                .concat([...document.querySelectorAll(`#section-pane-${sectionId} .question-content textarea`)])
            return prev
        }, {}) ?? {}

        topicSubmission?.tableResponses?.forEach((resp) => {
            const respInput = inputsBySection[resp.sectionId]?.[resp.tableNumber - 1]
            if (respInput) {
                if (respInput.type === "checkbox") {
                    respInput.checked = resp.answer === "on"
                } else {
                    respInput.value = resp.answer
                }
            }
        })
    }, [topicSubmission?.tableResponses])

    const embeddedQuestionCount = lessonSubmission?.responses?.findIndex((response) => response.section === 2) - 1

    if (state.submission.status !== "graded" && state.submission.status !== "rework-required" && state.submission.status !== "rejected") return (<>
        <Row className="sticky-top bg-white pt-3">
            <Col sm={5}>
                <div className="text-start mb-3">
                    <Link to={state.previousPage ?? ".."} state={studentSubmissionsState} className="text-decoration-none">
                        <i className="fas fa-arrow-left" />
                        <span> Return to Submissions</span>
                    </Link>
                </div>
            </Col>
        </Row>
        <Row>
            <Col>
                <h2>Grading is in progress. Please check back later for your grade.</h2>
            </Col>
        </Row>
    </>)

    return <>
        <Row className="sticky-top bg-white pt-3">
            <Col sm={5}>
                <div className="text-start mb-3">
                    <Link to={state.previousPage ?? ".."} state={studentSubmissionsState} className="text-decoration-none d-print-none">
                        <i className="fas fa-arrow-left" />
                        <span> Return to Submissions</span>
                    </Link>
                </div>
            </Col>
        </Row>
        <Row className="text-start overflow-auto">
            <Col xs={{ span: 12, order:  2}} md={{ span: 9, order: 2 }}>
                    <h1 className="h3 text-start fw-bold text-center rounded"><span className="mark">{state.assignment?.title ?? ""}</span> Submission</h1>
                    {(lessonSubmission || topicSubmission || assessmentSubmission) && <div className="mt-3">
                        {topicSubmission && <>
                            {topicSubmission.sections.map((section) => {
                                if (section.type === "Lesson" && lessonSubmission) {
                                    return <Pane className="shadow p-4 mt-3">
                                        <StudentLessonResponses
                                            expand={expand}
                                            setExpand={setExpand}
                                            assessmentViewOption={assessmentViewOption}
                                            lessonSubmission={lessonSubmission}
                                            embeddedQuestionCount={embeddedQuestionCount}
                                            topicSectionHeader={section.header}
                                        />
                                        <StudentLessonTableResponses
                                            expand={expand}
                                            lessonSubmission={lessonSubmission} />
                                    </Pane>
                                } else if (section.type === "Assessment" && assessmentViewOption !== "Grade" && section.assessmentSubmission) {
                                    return <Pane className="mt-3 shadow p-4">
                                        <StudentAssessmentResponses
                                            assessmentSubmission={section.assessmentSubmission}
                                            submissionStatus={state.submission.status}
                                            assessmentViewOption={assessmentViewOption}
                                            topicSectionHeader={section.header}
                                            viewType="normal" />
                                    </Pane>
                                } else if (section.type === "Questions") {
                                    return <Pane className="mt-3 shadow p-4">
                                        <div id={`section-pane-${section.id}`}>
                                            <StudentTopicSectionResponses
                                                assessmentViewOption={assessmentViewOption}
                                                section={section}
                                                topicSubmission={topicSubmission}
                                                topic={topic}
                                            />
                                        </div>
                                    </Pane>
                                } else {
                                    return null
                                }
                            })}
                        </>}

                        {!topicSubmission && <>
                            {lessonSubmission &&
                                <Pane className="shadow p-4">
                                    <StudentLessonResponses
                                        expand={expand}
                                        setExpand={setExpand}
                                        assessmentViewOption={assessmentViewOption}
                                        lessonSubmission={lessonSubmission}
                                        embeddedQuestionCount={embeddedQuestionCount}
                                    />
                                    <StudentLessonTableResponses
                                        expand={expand}
                                        lessonSubmission={lessonSubmission} />
                                </Pane>}

                            {assessmentViewOption !== "Grade" && assessmentSubmission &&
                                <Pane className="mt-3 shadow p-4">
                                    <StudentAssessmentResponses
                                        submissionStatus={state.submission.status}
                                        assessmentSubmission={assessmentSubmission}
                                        assessmentViewOption={assessmentViewOption}
                                        viewType="normal" />
                                </Pane>}
                        </>}
                </div>}
            </Col>

            {/* Right side bar for grade information */}
            {(lessonSubmission || topicSubmission || assessmentSubmission) && 
            <Col xs={{ span: 12, order:  1 }} md={{ span: 3, order: 2 }} className={`px-4 ${styles.lessonGradeContainer}`}>
                <StudentSubmissionSidebar
                    assignmentSubmission={state.submission}
                    topicSubmission={topicSubmission}
                    lessonSubmission={lessonSubmission}
                    assessmentSubmission={assessmentSubmission}
                    assessmentOnly={assessmentOnly}
                    startReworkSubmission={() => openAssignment({ assignment: state.assignment })}
                    />
            </Col>}
        </Row>
    </>
}
